import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, TextField, MenuItem, FormControl, InputLabel, Select, Grid, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Radio, RadioGroup, FormControlLabel, FormLabel } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TaskCard from '../components/TaskCard';
import { Task } from '../types/task';
import { useAuth } from '../contexts/AuthContext';
import FormattingGuide from '../components/FormattingGuide';
import { TaskService } from '../services/taskService';
import { authService } from '../services/authService';
import { ThemeCustomizerButton } from '../components/ThemeCustomizer';
import TimeDisplay from '../components/TimeDisplay';
import ThemeToggle from '../components/ThemeToggle';

const WorkerDashboard: React.FC = () => {
	const navigate = useNavigate();
	const auth = useAuth();
	const [tasks, setTasks] = useState<Task[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [selectedType, setSelectedType] = useState<string>('All');
	const [sortOption, setSortOption] = useState<string>('Newest First');
	const [selectionMode, setSelectionMode] = useState<boolean>(false);
	const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
	const [rejectionDialogOpen, setRejectionDialogOpen] = useState<boolean>(false);
	const [rejectionReason, setRejectionReason] = useState<string>('instructed_to_reject');
	
	// Create a local TaskService instance for batch operations
	const taskServiceRef = useRef(new TaskService(authService));

	useEffect(() => {
		const fetchTasks = async () => {
			try {
				setLoading(true);
				const fetchedTasks = await auth.taskService.getWorkerTasks();
				setTasks(fetchedTasks);
				setError(null);
			} catch (err) {
				setError('Failed to fetch tasks. Please try again later.');
				console.error('Error fetching tasks:', err);
			} finally {
				setLoading(false);
			}
		};

		fetchTasks();
	}, [auth.taskService]);

	const handleRefresh = async () => {
		setError(null);
		setTasks([]);
		setLoading(true);
		try {
			const fetchedTasks = await auth.taskService.getWorkerTasks();
			setTasks(fetchedTasks);
		} catch (err) {
			setError('Failed to fetch tasks. Please try again later.');
		} finally {
			setLoading(false);
		}
	};

	const handleTaskCompleted = (task_id: string) => {
		setTasks(prevTasks => prevTasks.filter(task => task.task_id !== task_id));
		// Also remove from selected tasks if it was selected
		setSelectedTaskIds(prev => prev.filter(id => id !== task_id));
	};

	const handleLogout = () => {
		auth.logout();
		navigate('/');
	};

	const toggleSelectionMode = () => {
		setSelectionMode(!selectionMode);
		// Clear selections when exiting selection mode
		if (selectionMode) {
			setSelectedTaskIds([]);
		}
	};

	const toggleTaskSelection = (taskId: string) => {
		setSelectedTaskIds(prev => 
			prev.includes(taskId) 
				? prev.filter(id => id !== taskId) 
				: [...prev, taskId]
		);
	};

	const toggleSelectAll = () => {
		if (selectedTaskIds.length === filteredTasks.length) {
			// Deselect all
			setSelectedTaskIds([]);
		} else {
			// Select all filtered tasks
			setSelectedTaskIds(filteredTasks.map(task => task.task_id));
		}
	};

	const openRejectionDialog = () => {
		if (selectedTaskIds.length === 0) return;
		console.log('Opening rejection dialog with current reason:', rejectionReason);
		setRejectionDialogOpen(true);
	};

	const handleRejectionDialogClose = () => {
		setRejectionDialogOpen(false);
	};

	const handleRejectSelectedTasks = async () => {
		if (selectedTaskIds.length === 0) return;
		
		// Close the dialog
		setRejectionDialogOpen(false);
		
		// Debug: Log the selected rejection reason
		console.log('Bulk rejection with reason:', rejectionReason);
		
		try {
			setLoading(true);
			setError(null); // Clear previous errors
			
			// Check if user is authenticated before proceeding
			if (!auth.isAuthenticated) {
				setError('You must be logged in to reject tasks. Please refresh the page and log in again.');
				return;
			}
			
			// Use our local TaskService instance instead of the one from auth context
			// Pass the rejection reason selected by the user
			const results = await taskServiceRef.current.rejectMultipleTasks(selectedTaskIds, rejectionReason);
			
			// Remove the successfully rejected tasks from the list
			if (results.successful.length > 0) {
				setTasks(prev => prev.filter(task => !results.successful.includes(task.task_id)));
				
				// Update selected tasks to remove successfully rejected ones
				setSelectedTaskIds(prev => prev.filter(id => !results.successful.includes(id)));
			}
			
			// Show results to the user
			if (results.failed.length > 0) {
				if (results.successful.length > 0) {
					setError(`${results.successful.length} task${results.successful.length === 1 ? '' : 's'} rejected successfully. ${results.failed.length} task${results.failed.length === 1 ? '' : 's'} failed to reject.`);
				} else {
					setError(`Failed to reject ${results.failed.length} task${results.failed.length === 1 ? '' : 's'}. Please check your connection and try again.`);
				}
			} else if (results.successful.length > 0) {
				// All tasks were successfully rejected
				setError(null);
				// Exit selection mode after completely successful operation
				setSelectionMode(false);
			} else {
				// No tasks were processed (shouldn't happen with our implementation)
				setError('No tasks were processed. Please try again.');
			}
		} catch (err: any) {
			console.error('Error in batch rejection:', err);
			setError(`Failed to reject tasks: ${err?.message || 'Unknown error'}. Please check your connection and try again.`);
		} finally {
			setLoading(false);
		}
	};

	const filterAndSortTasks = () => {
		let filteredTasks = tasks;

		// Apply search filter
		if (searchQuery) {
			filteredTasks = filteredTasks.filter((task) =>
				task.task_id.toLowerCase().includes(searchQuery.toLowerCase())
			);
		}

		// Apply document type filter
		if (selectedType !== 'All') {
			filteredTasks = filteredTasks.filter(
				(task) => task.document_type === selectedType
			);
		}

		// Apply sorting
		if (sortOption === 'Newest First') {
			filteredTasks.sort((a, b) =>
				(b.created_at || '').localeCompare(a.created_at || '')
			);
		} else if (sortOption === 'Oldest First') {
			filteredTasks.sort((a, b) =>
				(a.created_at || '').localeCompare(b.created_at || '')
			);
		} else if (sortOption === 'Document Type (A-Z)') {
			filteredTasks.sort((a, b) =>
				(a.document_type || '').localeCompare(b.document_type || '')
			);
		} else if (sortOption === 'Document Type (Z-A)') {
			filteredTasks.sort((a, b) =>
				(b.document_type || '').localeCompare(a.document_type || '')
			);
		}

		return filteredTasks;
	};

	const filteredTasks = filterAndSortTasks();

	return (
		<Container maxWidth="xl" sx={{ pt: 4 }}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
				<Typography variant="h4">Data reviewer dashboard</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
					<TimeDisplay />
					<ThemeToggle sx={{ mr: 1 }} />
					<ThemeCustomizerButton />
					<Button 
						variant="outlined"
						onClick={handleLogout}
					>
						Logout
					</Button>
				</Box>
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
				<Button 
					startIcon={<RefreshIcon />} 
					onClick={handleRefresh} 
					disabled={loading}
					variant="contained"
				>
					Refresh Tasks
				</Button>
				{/* Formatting Guide Tooltip - positioned next to the Refresh Tasks button */}
				<FormattingGuide />

				{/* Selection mode toggle button - always in the same position */}
				<Button
					variant={selectionMode ? "contained" : "outlined"}
					color={selectionMode ? "secondary" : "primary"}
					onClick={toggleSelectionMode}
					size="medium"
					sx={{ ml: 'auto' }}
				>
					{selectionMode ? "Cancel Selection" : "Select Tasks"}
				</Button>
				<FormattingGuide userType={auth.userType} />
			</Box>

			{/* Additional action buttons that only appear in selection mode */}
			{selectionMode && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, justifyContent: 'flex-end' }}>
					{/* Select all button - always in the same position */}
					<Button
						variant="outlined"
						color="primary"
						onClick={toggleSelectAll}
						size="medium"
					>
						{selectedTaskIds.length === filteredTasks.length && filteredTasks.length > 0 ? "Deselect All" : "Select All"}
					</Button>
					
					{/* Reject selected button - only visible when tasks are selected */}
					<Button
						variant="contained"
						color="error"
						onClick={openRejectionDialog}
						size="medium"
						disabled={selectedTaskIds.length === 0}
					>
						Reject Selected {selectedTaskIds.length > 0 && `(${selectedTaskIds.length})`}
					</Button>
				</Box>
			)}

			{loading && <Typography>Loading tasks...</Typography>}
			{error && <Typography color="error">{error}</Typography>}

			{/* Rejection Reason Dialog */}
			<Dialog open={rejectionDialogOpen} onClose={handleRejectionDialogClose}>
				<DialogTitle>Reject {selectedTaskIds.length} Tasks</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Please select a reason for rejecting these tasks:
					</DialogContentText>
					<FormControl component="fieldset" sx={{ mt: 2 }}>
						<RadioGroup
							aria-label="rejection-reason"
							name="rejection-reason"
							value={rejectionReason}
							onChange={(e) => {
								const newReason = e.target.value;
								console.log('Rejection reason changed to:', newReason);
								setRejectionReason(newReason);
							}}
						>
							<FormControlLabel 
								value="instructed_to_reject" 
								control={<Radio />} 
								label="I was instructed to reject this request" 
							/>
							<FormControlLabel 
								value="not_qualified" 
								control={<Radio />} 
								label="I am not qualified for this task" 
							/>
						</RadioGroup>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleRejectionDialogClose}>Cancel</Button>
					<Button onClick={handleRejectSelectedTasks} color="error" variant="contained">
						Reject Tasks
					</Button>
				</DialogActions>
			</Dialog>
			{!loading && !error && tasks.length === 0 && <Typography>No tasks available.</Typography>}
			{!loading && tasks.length > 0 && (
				<Box>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
						<Box sx={{ display: 'flex', gap: 2 }}>
							<TextField
								label="Search Task ID"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								size="small"
							/>
							<Select
								value={selectedType}
								onChange={(e) => setSelectedType(e.target.value)}
								size="small"
								sx={{ minWidth: 200 }}
							>
								<MenuItem value="All">All request types</MenuItem>
								{[...new Set(tasks.map((task) => task.document_type))].map((type) => (
									<MenuItem key={type} value={type}>{type}</MenuItem>
								))}
							</Select>
							<Select
								value={sortOption}
								onChange={(e) => setSortOption(e.target.value)}
								size="small"
								sx={{ minWidth: 200 }}
							>
								<MenuItem value="Newest First">Newest first</MenuItem>
								<MenuItem value="Oldest First">Oldest first</MenuItem>
								<MenuItem value="Document Type (A-Z)">Document type (A-Z)</MenuItem>
								<MenuItem value="Document Type (Z-A)">Document type (Z-A)</MenuItem>
							</Select>
						</Box>

						{/* Task count information - right justified */}
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							{filteredTasks.length !== tasks.length && (
								<Typography variant="body1" sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', mr: 2 }}>
									<strong>Filtered tasks:</strong> {filteredTasks.length}
								</Typography>
							)}
							<Typography variant="body1" sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
								<strong>Total tasks:</strong> {tasks.length}
							</Typography>
						</Box>
					</Box>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						{filteredTasks.map((task) => (
							<TaskCard 
								key={task.task_id} 
								task={task} 
								onTaskCompleted={handleTaskCompleted}
								selectionMode={selectionMode}
								isSelected={selectedTaskIds.includes(task.task_id)}
								onToggleSelection={() => toggleTaskSelection(task.task_id)}
							/>
						))}
					</Box>
				</Box>
			)}
		</Container>
	);
};

export default WorkerDashboard;
